import React from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { Form } from "react-final-form";
import { SelectData } from "mui-rff";

import { useTranslation } from "@/lib/i18n";
import { useCreateDashboardMutation, useExtranetsQuery } from "@/features/admin/hooks";
import { Select, TextField, showToast } from "@/features/shared/components";

interface FormValues {
  name: string;
  extranetId: string;
}

type Props = {
  open: boolean;
  onClose: () => void;
};

export default function NewDashboardDialog({ open, onClose }: Props) {
  const { t } = useTranslation();
  const extranetsQuery = useExtranetsQuery();
  const createDashboardMutation = useCreateDashboardMutation();

  const extranetsOptions = React.useMemo<SelectData[]>(
    () =>
      extranetsQuery.data?.map(({ name, id }) => ({
        label: String(name),
        value: id,
      })) ?? [],
    [extranetsQuery.data],
  );

  async function onSubmit({ extranetId, ...rest }: FormValues) {
    createDashboardMutation.mutate(
      {
        extranetId,
        body: rest,
      },
      {
        onSuccess: ({ data: dashboard }) => {
          showToast(t("admin.messages.dashboard_created", { name: dashboard.name }));
          onClose();
        },
      },
    );
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <Form<FormValues>
        onSubmit={onSubmit}
        render={({ handleSubmit }) => (
          <>
            <DialogTitle>{t("admin.new_dashboard_dialog.title")}</DialogTitle>
            <DialogContent>
              <form onSubmit={handleSubmit} noValidate>
                <TextField
                  autoFocus
                  margin="dense"
                  name="name"
                  label={t("admin.new_dashboard_dialog.name_field.label")}
                  required
                />
                <Select
                  required
                  name="extranetId"
                  label={t("admin.new_dashboard_dialog.extranet_field.label")}
                  options={extranetsOptions}
                />
              </form>
            </DialogContent>
            <DialogActions>
              <Button
                disabled={createDashboardMutation.isPending}
                key="cancel"
                onClick={onClose}
              >
                {t("general.cancel")}
              </Button>
              <Button
                disabled={createDashboardMutation.isPending}
                key="save"
                onClick={handleSubmit}
              >
                {t("general.save")}
              </Button>
            </DialogActions>
          </>
        )}
      />
    </Dialog>
  );
}
